import type { Notification, Quote, Job } from '@/types';

class NotificationService {
  private notifications: Notification[] = [];
  private listeners: ((notifications: Notification[]) => void)[] = [];

  // Subscribe to notification updates
  subscribe(callback: (notifications: Notification[]) => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  // Notify all listeners
  private notifyListeners() {
    this.listeners.forEach(listener => listener([...this.notifications]));
  }

  // Create notification
  async createNotification(
    userId: string,
    type: Notification['type'],
    title: string,
    message: string,
    data?: Notification['data'],
    _sendEmail: boolean = true
  ): Promise<Notification> {
    const notification: Notification = {
      id: Date.now().toString() + Math.random().toString(36).substring(2, 11),
      userId,
      type,
      title,
      message,
      data,
      read: false,
      createdAt: new Date()
    };

    this.notifications.unshift(notification);
    this.notifyListeners();
    
    // Note: Email notifications are handled by the backend and Cloudflare Worker
    // This service only manages in-app notifications
    console.log(`📧 Notification created for ${userId}:`, { title, message });
    
    return notification;
  }

  // Get notifications for user
  getNotificationsForUser(userId: string): Notification[] {
    return this.notifications.filter(n => n.userId === userId);
  }

  // Mark notification as read
  markAsRead(notificationId: string) {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.read = true;
      this.notifyListeners();
    }
  }

  // Quote acceptance workflow notifications
  sendQuoteAcceptedNotification(quote: Quote, job: Job, providerId: string) {
    this.createNotification(
      providerId,
      'quote_accepted',
      '🎉 Quote Accepted!',
      `Congratulations! Your quote for "${job.title}" has been accepted. Please confirm your availability within 24 hours.`,
      {
        jobId: job.id,
        quoteId: quote.id,
        customerId: job.customerId
      }
    );

    // Schedule reminder notification (in real app, this would be handled by backend)
    setTimeout(() => {
      this.sendConfirmationReminder(quote, job, providerId);
    }, 23 * 60 * 60 * 1000); // 23 hours later
  }

  sendConfirmationReminder(quote: Quote, job: Job, providerId: string) {
    // Only send if quote is still in ACCEPTED status (not confirmed or expired)
    if (quote.status === 'ACCEPTED') {
      this.createNotification(
        providerId,
        'quote_confirmation_reminder',
        '⏰ Confirmation Reminder',
        `Reminder: Please confirm your availability for "${job.title}" within 1 hour or your quote will expire.`,
        {
          jobId: job.id,
          quoteId: quote.id,
          customerId: job.customerId
        }
      );
    }
  }

  sendQuoteExpiredNotification(quote: Quote, job: Job, providerId: string) {
    this.createNotification(
      providerId,
      'quote_expired',
      '⚠️ Quote Expired',
      `Your quote for "${job.title}" has expired due to no confirmation. The customer may select another provider.`,
      {
        jobId: job.id,
        quoteId: quote.id,
        customerId: job.customerId
      }
    );
  }

  sendQuoteRejectedNotification(quote: Quote, job: Job, providerId: string, reason: 'customer_selected_other' | 'booking_confirmed') {
    const message = reason === 'booking_confirmed' 
      ? `Thank you for your quote for "${job.title}". Another provider has been selected and the booking is now confirmed.`
      : `Thank you for your quote for "${job.title}". The customer has selected another provider.`;

    this.createNotification(
      providerId,
      'quote_rejected',
      '📋 Quote Update',
      message,
      {
        jobId: job.id,
        quoteId: quote.id,
        customerId: job.customerId
      }
    );
  }

  sendBookingConfirmedNotification(quote: Quote, job: Job, customerId: string) {
    this.createNotification(
      customerId,
      'booking_confirmed',
      '✅ Booking Confirmed',
      `Great! Your booking for "${job.title}" has been confirmed. The provider will contact you soon.`,
      {
        jobId: job.id,
        quoteId: quote.id,
        providerId: quote.providerId
      }
    );
  }

  // Customer notifications
  sendProviderNotConfirmedNotification(quote: Quote, job: Job, customerId: string) {
    this.createNotification(
      customerId,
      'quote_expired',
      '⏳ Provider Didn\'t Confirm',
      `The selected provider for "${job.title}" didn't confirm within 24 hours. You can now select another quote.`,
      {
        jobId: job.id,
        quoteId: quote.id,
        providerId: quote.providerId
      }
    );
  }

  // Job notification methods
  sendNewJobNotification(
    providerId: string, 
    job: Job, 
    phase: 'premium_only' | 'all_providers' | 'expanded_radius',
    title: string,
    message: string
  ) {
    this.createNotification(
      providerId,
      'new_job_available',
      title,
      message,
      {
        jobId: job.id,
        notificationPhase: phase
      }
    );
  }

  sendJobBroadcastNotification(providerId: string, job: Job, message: string) {
    this.createNotification(
      providerId,
      'job_broadcast',
      `📢 Job Broadcast: ${job.category}`,
      message,
      {
        jobId: job.id
      }
    );
  }
}

export const notificationService = new NotificationService();